import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';

import { NotificationService, NotificationSettings } from '@progress/kendo-angular-notification';
import { Observable, exhaustMap, switchMap, tap } from 'rxjs';
import { ICreateWorkTimeRequest, ISearchWorkTimes, IUpdateWorkTimeRequest, IWorkTime } from 'src/app/employee/models/work-time';
import { WorkTimeService } from 'src/app/employee/services/work-time.service';


export interface WorkTimesState {
  workTimes: IWorkTime[];
  isLoading: boolean;
}

@Injectable()
export class WorkTimesStore extends ComponentStore<WorkTimesState> {
  constructor(
    private workTimeService: WorkTimeService,
    private notification: NotificationService,
    private translocoService: TranslocoService,
    private router: Router,
    ) {
    super({
      workTimes: [],
      isLoading: false,
    });
  }

  readonly workTimes$ = this.select(({ workTimes }) => workTimes);

  readonly isLoading$ = this.select(({ isLoading }) => isLoading);


  setIsLoading = this.updater((state) => ({ ...state, isLoading: true }));

  setError = this.updater((state, error: HttpErrorResponse) => ({
    ...state,
    isLoading: false,
    error: error.message,
  }));


  readonly setWorkTimes = this.updater((state, workTimes: IWorkTime[]) => ({
    ...state,
    isLoading: false,
    errorData: null,
    workTimes: workTimes
  }));

  readonly setWorkTime = this.updater((state, workTime: IWorkTime) => ({
    ...state,
    isLoading: false,
    errorData: null,
    workTimes: [...state.workTimes, workTime],
    // totalResults: state.totalResults + 1
  }));

  readonly updateWorkTimeUpdater = this.updater((state, workTime: IWorkTime) => ({
    ...state,
    isLoading: false,
    errorData: null,
    workTimes: state.workTimes.map(existinItem =>
      existinItem.id === workTime.id ? workTime : existinItem),
  }));

  readonly deleteWorkTimeUpdater = this.updater((state, idToRemove: string) => ({
    ...state,
    isLoading: false,
    errorData: null,
    workTimes: state.workTimes.filter(workTime =>
      workTime.id !== idToRemove),
  }));

  getWorkTimes = this.effect((getWorkTimesRequest$: Observable<ISearchWorkTimes>) => {
    return getWorkTimesRequest$.pipe(
      tap(() => {
        this.setIsLoading();
      }),
      exhaustMap((request) => {
        return this.workTimeService.searchWorkTimes(request).pipe(
          tapResponse(
            (response) => {
              this.setWorkTimes(response.data);
            },
            (err: HttpErrorResponse) => this.setError(err)
          )
        );
      })
    );
  });

  createWorkTime = this.effect((createWorkTime$: Observable<ICreateWorkTimeRequest>) => {
    return createWorkTime$.pipe(
      tap(() => {
        this.setIsLoading();
      }),
      switchMap((request) => {
        return this.workTimeService.createWorkTime(request)
          .pipe(
            tapResponse(
              (response) => {
                    this.setWorkTime(response.workTime);
                    const settings: NotificationSettings = {
                      content: this.translocoService.translate('employee.workTimes.saveSuccess'),
                      hideAfter: 4000,
                      cssClass: "notification",
                      position: { horizontal: "center", vertical: "top" },
                      animation: { type: "fade", duration: 250 },
                      type: { style: "success", icon: true },
                    };
                
                    this.notification.show(settings);
              },
              (err: HttpErrorResponse) => this.setError(err)
            )
        );
      })
    );
  });

  updateWorkTime = this.effect((updateWorkTime$: Observable<IUpdateWorkTimeRequest>) => {
    return updateWorkTime$.pipe(
      tap(() => {
        this.setIsLoading();
      }),
      switchMap((request) => {
        return this.workTimeService.updateWorkTime(request)
          .pipe(
            tapResponse(
              (response) => {
                    this.updateWorkTimeUpdater(response.workTime);
                    const settings: NotificationSettings = {
                      content: this.translocoService.translate('employee.workTimes.saveSuccess'),
                      hideAfter: 4000,
                      cssClass: "notification",
                      position: { horizontal: "center", vertical: "top" },
                      animation: { type: "fade", duration: 250 },
                      type: { style: "success", icon: true },
                    };
                
                    this.notification.show(settings);
              },
              (err: HttpErrorResponse) => this.setError(err)
            )
        );
      })
    );
  });


  deleteWorkTime = this.effect((deleteWorkTime$: Observable<string>) => {
    return deleteWorkTime$.pipe(
      tap(() => {
        this.setIsLoading();
      }),
      exhaustMap((id) => {
        return this.workTimeService.deleteWorkTime(id).pipe(
          tapResponse(
            (response) => {
              this.deleteWorkTimeUpdater(id);
              const settings: NotificationSettings = {
                content: this.translocoService.translate('employee.workTimes.deleteSuccess'),
                hideAfter: 4000,
                cssClass: "notification",
                position: { horizontal: "center", vertical: "top" },
                animation: { type: "fade", duration: 250 },
                type: { style: "success", icon: true },
              };
          
              this.notification.show(settings);
            },
            (err: HttpErrorResponse) => this.setError(err)
          )
        );
      })
    );
  });

}
